<script setup lang="ts">
import { Filter } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

export interface FiltersState {
    source: string | null; // 'zotero' | 'manual' | null
    yearFrom: number | null;
    yearTo: number | null;
    hasPdf: boolean | null;
    citedOnly: boolean | null;
    collection: string | null;
}

const props = defineProps<{
    collections: string[];
    activeCount: number;
}>();

const emit = defineEmits<{
    change: [filters: FiltersState];
}>();

const open = ref(false);

const source = ref<string | null>(null);
const yearFrom = ref<string>('');
const yearTo = ref<string>('');
const hasPdf = ref<boolean | null>(null);
const citedOnly = ref<boolean | null>(null);
const collection = ref<string | null>(null);

function apply() {
    emit('change', {
        source: source.value,
        yearFrom: yearFrom.value ? parseInt(yearFrom.value) : null,
        yearTo: yearTo.value ? parseInt(yearTo.value) : null,
        hasPdf: hasPdf.value,
        citedOnly: citedOnly.value,
        collection: collection.value,
    });
    open.value = false;
}

function reset() {
    source.value = null;
    yearFrom.value = '';
    yearTo.value = '';
    hasPdf.value = null;
    citedOnly.value = null;
    collection.value = null;
    apply();
}

const hasActiveFilters = computed(() => props.activeCount > 0);
</script>

<template>
    <Popover v-model:open="open">
        <PopoverTrigger as-child>
            <Button size="sm" variant="outline" class="h-8 gap-1.5 text-xs">
                <Filter class="h-3 w-3" />
                Filtres
                <Badge
                    v-if="hasActiveFilters"
                    variant="default"
                    class="ml-0.5 h-4 min-w-4 px-1 text-[10px]"
                >
                    {{ activeCount }}
                </Badge>
            </Button>
        </PopoverTrigger>

        <PopoverContent class="w-64 space-y-4 p-4">
            <p
                class="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
            >
                Filtres
            </p>

            <!-- Source -->
            <div class="space-y-1.5">
                <label class="text-xs font-medium">Source</label>
                <div class="flex flex-wrap gap-1.5">
                    <Button
                        v-for="opt in [
                            { label: 'Tous', value: null },
                            { label: 'Zotero', value: 'zotero' },
                            { label: 'Manuel', value: 'manual' },
                        ]"
                        :key="String(opt.value)"
                        size="sm"
                        :variant="source === opt.value ? 'default' : 'outline'"
                        class="h-7 text-xs"
                        @click="source = opt.value"
                    >
                        {{ opt.label }}
                    </Button>
                </div>
            </div>

            <Separator />

            <!-- Year range -->
            <div class="space-y-1.5">
                <label class="text-xs font-medium">Année</label>
                <div class="flex items-center gap-2">
                    <input
                        v-model="yearFrom"
                        type="number"
                        placeholder="De"
                        min="1000"
                        max="9999"
                        class="h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:ring-1 focus:ring-ring focus:outline-none"
                    />
                    <span class="text-xs text-muted-foreground">–</span>
                    <input
                        v-model="yearTo"
                        type="number"
                        placeholder="À"
                        min="1000"
                        max="9999"
                        class="h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:ring-1 focus:ring-ring focus:outline-none"
                    />
                </div>
            </div>

            <Separator />

            <!-- Collection Zotero -->
            <div v-if="collections.length" class="space-y-1.5">
                <label class="text-xs font-medium">Collection Zotero</label>
                <select
                    v-model="collection"
                    class="h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:ring-1 focus:ring-ring focus:outline-none"
                >
                    <option :value="null">Toutes</option>
                    <option v-for="col in collections" :key="col" :value="col">
                        {{ col }}
                    </option>
                </select>
            </div>

            <Separator />

            <!-- Toggles -->
            <div class="space-y-2">
                <label class="flex cursor-pointer items-center gap-2 text-xs">
                    <input
                        v-model="hasPdf"
                        type="checkbox"
                        :true-value="true"
                        :false-value="null"
                        class="rounded"
                    />
                    A un PDF
                </label>
                <label class="flex cursor-pointer items-center gap-2 text-xs">
                    <input
                        v-model="citedOnly"
                        type="checkbox"
                        :true-value="true"
                        :false-value="null"
                        class="rounded"
                    />
                    Déjà cité dans le document
                </label>
            </div>

            <!-- Actions -->
            <div class="flex gap-2 pt-1">
                <Button
                    size="sm"
                    variant="outline"
                    class="h-7 flex-1 text-xs"
                    @click="reset"
                >
                    Réinitialiser
                </Button>
                <Button size="sm" class="h-7 flex-1 text-xs" @click="apply">
                    Appliquer
                </Button>
            </div>
        </PopoverContent>
    </Popover>
</template>
